<template>
  <textarea
    ref="statementEditField"
    v-model="editedText"
    class="form-control gb-text-3 pl-textarea"
    :id="textareaId"
    :class="ignoreBlurClass"
    @keyup="$emit('editedText', editedText)"
    @blur="onBlur"
    @input="adjustHeight"
  />
</template>

<script>
import MarkdownIt from 'markdown-it'
import autosize from 'autosize'
import DOMPurify from 'dompurify'
import { wikiToPlainText, plainToWikiTextMappings } from '@/util/tokenize_wiki_text'
import { SharedClassNames } from '@/util/shared_classnames'

let md = new MarkdownIt({ html: true })

// convert wiki text to plain text, storing mappings
// strip markdown, replace line breaks with \n\n to preserve paragraph breaks (and convert single line breaks to paragraph breaks)
function sanitizeText(text) {
  text = DOMPurify.sanitize(text, {ALLOWED_TAGS: ['b', 'br', 'sub', 'sup', 'i', 'ul', 'ol', 'li', 'p', 'u', 'q']})
  let sanitizedText = wikiToPlainText(text)
  let renderedMarkdown = md.render(sanitizedText)

  let div = document.createElement('div')
  div.innerHTML = renderedMarkdown
  sanitizedText = div.textContent.replace(/\n/g, '\n\n').replace(/\n{3,}/g, '\n\n') // this preserves line breaks in the form of new paragraphs when re-rendered
  return sanitizedText.trim()
}

export default {
  name: 'WikiTextArea',
  props: {
    text: {
      type: String,
      required: true
    },
    textareaId: {
      type: String,
      default: undefined
    },
  },
  data() {
    return {
      editedText: sanitizeText(this.text),
      replacementMappings: plainToWikiTextMappings(this.text),
      baseScrollHeight: 0,
      ignoreBlurClass: SharedClassNames.IGNORE_BLUR
    }
  },
  mounted() {
    let textarea = this.$refs.statementEditField
    autosize(this.$refs.statementEditField)
    textarea.focus()
  },
  watch: {
    text: function(newVal) {
      this.sanitizeTextForEditing()
    }
  },
  methods: {
    onBlur(e) {
      const shouldParentIgnoreBlur = e.relatedTarget && e.relatedTarget.classList.contains(SharedClassNames.IGNORE_BLUR)
      const returnFocus = e.relatedTarget && e.relatedTarget.classList.contains(SharedClassNames.RETURN_FOCUS)
      let editedText = sanitizeText(this.editedText)

      Object.keys(this.replacementMappings).forEach(k => {
        editedText = editedText.replace(k, this.replacementMappings[k])
      })

      this.$emit('blurred', editedText, shouldParentIgnoreBlur)
      if (returnFocus) {
        setTimeout(
          // returns focus to textarea so the next blur will re-invoke this function
          () => this.$refs.statementEditField.focus(),
          250
        )
      }
    },
    sanitizeTextForEditing() {
      this.editedText = sanitizeText(this.text)
      this.replacementMappings = plainToWikiTextMappings(this.text)
    },
    adjustHeight() {
      autosize(this.$refs.statementEditField)
    }
  }
}
</script>

<style scoped lang="scss">
.pl-textarea {
  resize: none;
  overflow: hidden;
  background: lightyellow;
}
</style>
